import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, SpecraBadge, SpecraButton, SpecraInlineBanner, SpecraLink, SpecraOverlayDialog, SpecraPageHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { MoreHorizontal, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ConvertToProposalDialog } from '../../../../components/Capture/ConvertToProposalDialog';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { CaptureLifecyclePanel } from '../../CaptureLifecyclePanel';
import { useCaptureWorkspace } from '../../hooks';
import { TouchpointSheet } from '../../TouchpointSheet';
import { MergePursuitDialog } from '../surgery/MergePursuitDialog';
import { MovePursuitComponentsDialog } from '../surgery/MovePursuitComponentsDialog';
import { SplitPursuitDialog } from '../surgery/SplitPursuitDialog';
import { useLogTouchpointDeepLink } from '../useLogTouchpointDeepLink';
const ACTION_ITEMS = [
    {
        action: 'park',
        label: 'Park…',
        testId: Testids.pursuitHome.moreItemPark,
        when: [
            'active'
        ]
    },
    {
        action: 'close',
        label: 'Close (no-bid)…',
        testId: Testids.pursuitHome.moreItemClose,
        when: [
            'active',
            'parked'
        ]
    },
    {
        action: 'resume',
        label: 'Resume',
        testId: Testids.pursuitHome.moreItemResume,
        when: [
            'parked',
            'closed'
        ]
    },
    {
        action: 'record_outcome',
        label: 'Record outcome…',
        testId: Testids.pursuitHome.moreItemRecordOutcome,
        when: [
            'active',
            'parked',
            'closed'
        ]
    }
];
/** The lifecycle actions the More menu offers for a lifecycle (CaptureLifecyclePanel's own rules). */ export function lifecycleMenuActions(lifecycle) {
    return ACTION_ITEMS.filter((i)=>i.when.includes(lifecycle)).map((i)=>i.action);
}
/**
 * Pursuit Home header (GRW-16 item 2): name, labels and context line; Log
 * touchpoint (also opened by the OD-92 deep link) and the More menu
 * (lifecycle actions, Open proposal). Editors only see the actions.
 */ export function PursuitHomeHeader({ header, correlationId, setupHint }) {
    const { capabilities } = header;
    const id = header.captureProjectId;
    const { data } = useCaptureWorkspace(id);
    const deepLink = useLogTouchpointDeepLink({
        canEdit: capabilities.canEdit
    });
    const [sheetOpen, setSheetOpen] = useState(false);
    const [lifecycleAction, setLifecycleAction] = useState(null);
    const [convertOpen, setConvertOpen] = useState(false);
    // GRW-19 graph surgery (server re-checks update / create; merged stubs have canEdit=false).
    const [surgery, setSurgery] = useState(null);
    const items = useMemo(()=>capabilities.canEdit ? ACTION_ITEMS.filter((i)=>i.when.includes(header.lifecycle)) : [], [
        capabilities.canEdit,
        header.lifecycle
    ]);
    const showMore = items.length > 0 || capabilities.canConvertToProposal || capabilities.canEdit;
    const touchpointOpen = sheetOpen || deepLink.open;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                actions: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        capabilities.canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.pursuitHome.logTouchpoint,
                            iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                className: "h-4 w-4"
                            }),
                            onClick: ()=>setSheetOpen(true),
                            variant: "secondary",
                            children: "Log touchpoint"
                        }),
                        showMore && /*#__PURE__*/ _jsxs(DropdownMenu, {
                            children: [
                                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                    asChild: true,
                                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                                        "aria-label": "More actions",
                                        "data-testid": Testids.pursuitHome.more,
                                        iconLeft: /*#__PURE__*/ _jsx(MoreHorizontal, {
                                            className: "h-4 w-4"
                                        }),
                                        variant: "secondary",
                                        children: "More"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                    align: "end",
                                    children: [
                                        items.map((item)=>/*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                "data-testid": item.testId,
                                                onSelect: ()=>setLifecycleAction(item.action),
                                                children: item.label
                                            }, item.action)),
                                        capabilities.canEdit && capabilities.canCreatePursuit && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            "data-testid": Testids.pursuitHome.moreItemSplit,
                                            onSelect: ()=>setSurgery('split'),
                                            children: "Split pursuit…"
                                        }),
                                        capabilities.canEdit && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            "data-testid": Testids.pursuitHome.moreItemMerge,
                                            onSelect: ()=>setSurgery('merge'),
                                            children: "Merge into another pursuit…"
                                        }),
                                        capabilities.canEdit && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            "data-testid": Testids.pursuitHome.moreItemMove,
                                            onSelect: ()=>setSurgery('move'),
                                            children: "Move items to another pursuit…"
                                        }),
                                        capabilities.canConvertToProposal && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            "data-testid": Testids.pursuitHome.moreItemOpenProposal,
                                            onSelect: ()=>setConvertOpen(true),
                                            children: "Open proposal…"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                meta: /*#__PURE__*/ _jsxs("span", {
                    className: "flex flex-wrap items-center gap-[var(--space-2)]",
                    children: [
                        header.labels.map((l)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: l.tone || 'neutral',
                                children: l.label
                            }, l.key)),
                        header.contextLine && /*#__PURE__*/ _jsx(SpecraText, {
                            size: "body-ui",
                            tone: "muted",
                            children: header.contextLine
                        })
                    ]
                }),
                title: header.name,
                titleSize: "home"
            }),
            setupHint,
            touchpointOpen && /*#__PURE__*/ _jsx(TouchpointSheet, {
                captureProjectId: id,
                correlationId: correlationId,
                initialParticipants: deepLink.open ? deepLink.initialParticipants : undefined,
                interaction: null,
                onClose: ()=>{
                    setSheetOpen(false);
                    if (deepLink.open) deepLink.close();
                },
                pursuitName: header.name,
                zouProjectId: header.zouProjectId
            }),
            lifecycleAction && data?.summary && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                onOpenChange: (open)=>!open && setLifecycleAction(null),
                open: true,
                title: "Pursuit lifecycle",
                children: /*#__PURE__*/ _jsx(CaptureLifecyclePanel, {
                    initialAction: lifecycleAction,
                    summary: data.summary,
                    tasks: data.tasks ?? []
                }, `${data.summary.captureProjectId}:${lifecycleAction}`)
            }),
            /*#__PURE__*/ _jsx(SplitPursuitDialog, {
                captureProjectId: id,
                onOpenChange: (o)=>setSurgery(o ? 'split' : null),
                open: surgery === 'split',
                pursuitName: header.name
            }),
            /*#__PURE__*/ _jsx(MergePursuitDialog, {
                captureProjectId: id,
                onOpenChange: (o)=>setSurgery(o ? 'merge' : null),
                open: surgery === 'merge',
                pursuitName: header.name
            }),
            /*#__PURE__*/ _jsx(MovePursuitComponentsDialog, {
                captureProjectId: id,
                onOpenChange: (o)=>setSurgery(o ? 'move' : null),
                open: surgery === 'move'
            }),
            /*#__PURE__*/ _jsx(ConvertToProposalDialog, {
                captureProjectId: id,
                onOpenChange: setConvertOpen,
                open: convertOpen
            })
        ]
    });
}
/**
 * The setup hint shown in place of an empty KPI strip, for editors (GRW-16
 * item 3). "link the contract" opens the GRW-04 Link contract dialog, the
 * same one as the rail's Related action.
 */ export function PursuitSetupHint({ captureProjectId, onLinkContract }) {
    return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        "data-testid": Testids.pursuitHome.setupHint,
        tone: "info",
        variant: "framed",
        children: [
            /*#__PURE__*/ _jsx(SpecraLink, {
                href: `/capture/${captureProjectId}/decisions`,
                renderLink: renderRouterLink,
                children: "Schedule a gate"
            }),
            ",",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: onLinkContract,
                size: "inline",
                variant: "link",
                children: "link the contract this recompetes"
            }),
            ", or",
            ' ',
            /*#__PURE__*/ _jsx(SpecraLink, {
                href: `/capture/${captureProjectId}/rfi-responses`,
                renderLink: renderRouterLink,
                children: "open an RFI response"
            }),
            ' ',
            "to see them here."
        ]
    });
}
